import { useEffect, useState, type FormEvent } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { GET_USER_PROFILE } from '../../graphql';
import { loadProfilePhoto, saveProfilePhoto, uploadProfilePhoto } from '../../lib/profilePhoto';
import { hashPasswordClient } from '../../utils/passwordUtils';
import { Loader } from '../common/PageLoader';
import { RESEND_SECONDS } from '../account/TinycmsLogin';

const REQUEST_PASSWORD_CHANGE_CODE = gql`
  mutation RequestPasswordChangeCode {
    requestPasswordChangeCode
  }
`;

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($code: String!, $newPassword: String!) {
    changePassword(code: $code, newPassword: $newPassword)
  }
`;

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

const NOTIFICATIONS = [
  ['ask', 'An agent asks for approval', 'Email and in-app, as it happens'],
  ['digest', 'Daily digest', 'What agents published and changed, every morning at 8 AM'],
  ['fail', 'A publish is blocked', 'When checks stop a scheduled publish'],
] as const;

export function Profile() {
  const { loading, error, data } = useQuery<UserProfileData>(GET_USER_PROFILE);
  const email = data?.me?.email || '';
  const userId = data?.me?.id || '';
  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [notifs, setNotifs] = useState({ ask: true, digest: true, fail: true });

  const [changingPassword, setChangingPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordOk, setPasswordOk] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [hashing, setHashing] = useState(false);

  const [requestCode, { loading: requesting }] = useMutation(REQUEST_PASSWORD_CHANGE_CODE);
  const [changePassword, { loading: changing }] = useMutation(CHANGE_PASSWORD);

  useEffect(() => {
    if (email) setName(email.split('@')[0]);
  }, [email]);

  useEffect(() => {
    if (userId) setPhotoUrl(loadProfilePhoto(userId));
  }, [userId]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const initials = (name || email || 'ME').slice(0, 2).toUpperCase();

  const message = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const openPasswordChange = () => {
    setChangingPassword(true);
    setCodeSent(false);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordOk('');
  };

  const cancelPasswordChange = () => {
    setChangingPassword(false);
    setCodeSent(false);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const sendCode = async () => {
    setPasswordError('');
    setPasswordOk('');
    try {
      await requestCode();
      setCodeSent(true);
      setCode('');
      setCooldown(RESEND_SECONDS);
      setPasswordOk('A 6-digit code is on its way. It works for 10 minutes.');
    } catch (err) {
      setPasswordError(message(err, 'The code couldn’t be sent. Try again.'));
    }
  };

  const submitPasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) {
      setPasswordError('Enter the 6-digit code from the email.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Use at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Those passwords don’t match.');
      return;
    }
    setPasswordError('');
    setPasswordOk('');
    setHashing(true);
    try {
      await changePassword({
        variables: {
          code,
          newPassword: await hashPasswordClient(newPassword),
        },
      });
      setChangingPassword(false);
      setCodeSent(false);
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOk('Password updated.');
    } catch (err) {
      setPasswordError(message(err, 'Could not change the password. Try again.'));
    } finally {
      setHashing(false);
    }
  };

  if (loading) return <Loader label="Loading" className="ink-lede" />;
  if (error) return <p className="ink-lede">Could not load your profile.</p>;

  const busy = requesting || changing || hashing;

  return (
    <div>
      <h1 className="ink-h1">Profile</h1>
      <p className="ink-lede">Your sign-in and how tinycms reaches you.</p>

      <div className="ink-profile-photo">
        <span className="ink-profile-avatar">
          {photoUrl ? <img src={photoUrl} alt="" /> : initials}
        </span>
        <label className="ink-bill-link" style={{ cursor: 'pointer' }}>
          Change photo
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            disabled={uploading || !userId}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file || !userId) return;
              const preview = URL.createObjectURL(file);
              setPhotoUrl(preview);
              setPhotoError('');
              setUploading(true);
              void uploadProfilePhoto(file)
                .then((url) => {
                  saveProfilePhoto(userId, url);
                  setPhotoUrl(url);
                })
                .catch((err: unknown) => {
                  setPhotoUrl(loadProfilePhoto(userId));
                  setPhotoError(err instanceof Error ? err.message : 'Could not save that photo.');
                })
                .finally(() => {
                  URL.revokeObjectURL(preview);
                  setUploading(false);
                });
            }}
          />
        </label>
        {uploading ? <span className="ink-team-email">Saving photo…</span> : null}
        {photoError ? <span className="ink-team-email">{photoError}</span> : null}
      </div>

      <div className="ink-profile-grid">
        <label>
          Name
          <input
            className="ink-acct-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Email
          <input className="ink-acct-input" type="email" value={email} readOnly />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ font: '500 13px/1.3 var(--ink-font)' }}>Password</span>
          {!changingPassword ? (
            <button
              type="button"
              className="ink-btn"
              style={{ width: 'max-content' }}
              onClick={openPasswordChange}
            >
              Change password
            </button>
          ) : (
            <form
              onSubmit={submitPasswordChange}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}
            >
              <p className="ink-team-email" style={{ margin: 0 }}>
                We’ll email a code to {email}. Enter it below to unlock the new password.
              </p>
              {!codeSent ? (
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  style={{ width: 'max-content' }}
                  disabled={busy}
                  onClick={() => void sendCode()}
                >
                  {requesting ? 'Sending…' : 'Email me a code'}
                </button>
              ) : (
                <>
                  <label>
                    Code
                    <input
                      className="ink-acct-input"
                      value={code}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123 456"
                      maxLength={7}
                      autoFocus
                      onChange={(e) => {
                        setCode(e.target.value.replace(/[^\d ]/g, ''));
                        setPasswordError('');
                      }}
                    />
                  </label>
                  <label>
                    New password
                    <input
                      className="ink-acct-input"
                      type="password"
                      value={newPassword}
                      autoComplete="new-password"
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError('');
                      }}
                    />
                  </label>
                  <label>
                    Confirm password
                    <input
                      className="ink-acct-input"
                      type="password"
                      value={confirmPassword}
                      autoComplete="new-password"
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError('');
                      }}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="submit"
                      className="ink-btn ink-btn-primary"
                      disabled={busy}
                    >
                      {busy ? 'Saving…' : 'Update password'}
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-quiet"
                      disabled={busy || cooldown > 0}
                      onClick={() => void sendCode()}
                    >
                      {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send a new code'}
                    </button>
                  </div>
                </>
              )}
              <button
                type="button"
                className="ink-bill-link"
                style={{ width: 'max-content', background: 'none', border: 0, padding: 0 }}
                disabled={busy}
                onClick={cancelPasswordChange}
              >
                Cancel
              </button>
            </form>
          )}
          {passwordError ? (
            <span className="ink-team-email" style={{ color: 'var(--ink-danger, #b42318)' }}>
              {passwordError}
            </span>
          ) : null}
          {passwordOk && !passwordError ? (
            <span className="ink-team-email">{passwordOk}</span>
          ) : null}
        </div>
      </div>

      <h2 className="ink-bill-h">Notifications</h2>
      <div className="ink-profile-notifs">
        {NOTIFICATIONS.map(([key, label, desc]) => (
          <button
            key={key}
            type="button"
            className="ink-profile-notif"
            onClick={() => setNotifs((prev) => ({ ...prev, [key]: !prev[key] }))}
          >
            <span style={{ flex: 1 }}>
              <span className="ink-profile-notif-name">{label}</span>
              <span className="ink-profile-notif-desc">{desc}</span>
            </span>
            <span className="ink-profile-switch" data-on={notifs[key] ? 'true' : 'false'} />
          </button>
        ))}
      </div>
    </div>
  );
}
